import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldAlert, ShieldCheck, Zap, Wrench, ChevronRight, ArrowLeft,
  AlertTriangle, Clock, X, Send, History, Info, RefreshCw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { cn } from "../lib/utils";
import { fmtMDT, fmtMDTShort } from "../lib/time";

const SEVERITY_TONES = {
  critical: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30",
  high:     "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30",
  medium:   "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  low:      "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30",
  info:     "bg-muted text-muted-foreground border-border",
};

const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

function SeverityBadge({ severity, className }) {
  const sev = String(severity || "info").toLowerCase();
  return (
    <span className={cn(
      "inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide",
      SEVERITY_TONES[sev] || SEVERITY_TONES.info,
      className,
    )}>{severity || "info"}</span>
  );
}

function KpiCard({ label, value, tone, icon: Icon, sub }) {
  const toneClass = {
    rose:    "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300",
    orange:  "border-orange-500/30 bg-orange-500/5 text-orange-700 dark:text-orange-300",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
    primary: "border-primary/30 bg-primary/5 text-primary",
  }[tone] || "border-border bg-card text-foreground";
  return (
    <div className={cn("rounded-2xl border p-4 flex items-start gap-3", toneClass)} data-testid={`security-kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="w-9 h-9 rounded-xl bg-background/60 border border-current/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
        <div className="text-[26px] font-bold tabular-nums leading-none mt-1">{value}</div>
        {sub && <div className="text-[11.5px] opacity-70 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function FindingRow({ finding, onAskToFix, submitting }) {
  const [expanded, setExpanded] = useState(false);
  const history = finding.fix_requests || [];
  const latest = history[0];
  return (
    <li className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`finding-${finding.id}`}>
      <div className="p-4 flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <SeverityBadge severity={finding.severity} />
            <span className="text-[10.5px] font-mono text-muted-foreground uppercase">{finding.id}</span>
            {latest && (
              <span className="inline-flex items-center gap-1 rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300 px-2 py-0.5 text-[10.5px] font-semibold">
                <Wrench className="w-3 h-3" /> Fix requested · {latest.status}
              </span>
            )}
          </div>
          <h4 className="mt-1.5 text-[14.5px] font-semibold leading-snug">{finding.title}</h4>
          <p className="mt-1 text-[12.5px] text-muted-foreground leading-relaxed">{finding.description}</p>
          {expanded && (
            <div className="mt-3 space-y-3">
              {finding.remediation && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground">Remediation</div>
                  <p className="mt-1 text-[12.5px] leading-relaxed">{finding.remediation}</p>
                </div>
              )}
              {(finding.affected || []).length > 0 && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground">Affected</div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(finding.affected || []).map((a, i) => (
                      <code key={i} className="rounded bg-muted px-1.5 py-0.5 text-[11.5px] font-mono">{a}</code>
                    ))}
                  </div>
                </div>
              )}
              {history.length > 0 && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-wide font-semibold text-muted-foreground inline-flex items-center gap-1.5">
                    <History className="w-3 h-3" /> Fix request history
                  </div>
                  <ul className="mt-1.5 space-y-1.5">
                    {history.map((h) => (
                      <li key={h.id} className="rounded-lg border border-border bg-muted/40 p-2.5" data-testid={`fix-request-${h.id}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="text-[12px]">
                            <span className="font-semibold">{h.requested_by_name || h.requested_by}</span>
                            <span className="text-muted-foreground"> · {fmtMDTShort(h.created_at)}</span>
                          </div>
                          <span className="text-[10.5px] uppercase tracking-wide font-semibold rounded-full bg-background border border-border px-2 py-0.5">{h.status}</span>
                        </div>
                        {h.note && <p className="text-[12px] text-muted-foreground mt-1">{h.note}</p>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 text-[12px] font-semibold text-primary hover:underline"
            data-testid={`finding-toggle-${finding.id}`}
          >
            {expanded ? "Hide details" : "Show details"}
          </button>
        </div>
        <div className="md:w-52 shrink-0">
          <Button
            onClick={() => onAskToFix(finding)}
            disabled={submitting}
            className="w-full h-9 gap-1.5"
            data-testid={`ask-to-fix-btn-${finding.id}`}
          >
            <Zap className="w-3.5 h-3.5" /> Ask to fix
          </Button>
          {history.length > 0 && (
            <div className="mt-2 text-[11px] text-muted-foreground text-center">
              {history.length} previous request{history.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function AskToFixDialog({ finding, onClose, onSubmit, submitting }) {
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [finding?.id]);
  if (!finding) return null;
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="ask-to-fix-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-primary" /> Ask Emergent to fix
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <div className="flex items-center gap-2">
              <SeverityBadge severity={finding.severity} />
              <span className="text-[10.5px] font-mono text-muted-foreground uppercase">{finding.id}</span>
            </div>
            <div className="mt-1.5 text-[13.5px] font-semibold">{finding.title}</div>
            <p className="mt-1 text-[12px] text-muted-foreground">{finding.description}</p>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">Optional note</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Any context for the fixer (e.g., preferred approach, deadline, blast-radius concerns)"
              className="mt-1 text-[13px]"
              data-testid="ask-to-fix-note"
            />
          </div>
          <div className="rounded-md border border-sky-500/30 bg-sky-500/5 text-sky-800 dark:text-sky-200 p-2.5 text-[12px] flex gap-2">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              This queues the request into Mongo and appends it to the finding&apos;s history. Emergent will pick up pending requests from the log. No external call is made yet.
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="ask-to-fix-cancel">Cancel</Button>
          <Button onClick={() => onSubmit(note)} disabled={submitting} className="gap-1.5" data-testid="ask-to-fix-submit">
            <Send className="w-3.5 h-3.5" /> {submitting ? "Submitting…" : "Send request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReportDrawer({ reportId, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFinding, setActiveFinding] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/security/reports/${reportId}`);
      setReport(data);
    } catch { setReport(null); }
    finally { setLoading(false); }
  }, [reportId]);

  useEffect(() => { load(); }, [load]);

  const askToFix = async (note) => {
    if (!activeFinding) return;
    setSubmitting(true);
    try {
      await api.post(`/security/reports/${reportId}/findings/${activeFinding.id}/fix-request`, { note });
      toast({ title: "Fix request queued", description: `Logged for ${activeFinding.title}.` });
      setActiveFinding(null);
      await load();
    } catch (e) {
      toast({ title: "Could not queue request", description: e?.response?.data?.detail || "Try again in a moment.", variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  if (!reportId) return null;

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="security-report-drawer">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {report && (
                <div className="flex items-center gap-2 flex-wrap">
                  <SeverityBadge severity={report.severity} />
                  <span className="text-[10.5px] uppercase tracking-wide font-semibold rounded-full border border-border bg-muted/50 px-2 py-0.5">
                    {report.source}
                  </span>
                  <span className="text-[10.5px] text-muted-foreground">
                    <Clock className="w-3 h-3 inline mr-0.5" />
                    {fmtMDT(report.generated_at)}
                  </span>
                </div>
              )}
              <DialogTitle className="mt-2 text-[16px]">{loading ? "Loading…" : report?.title}</DialogTitle>
              {report?.summary && (
                <p className="mt-1 text-[12.5px] text-muted-foreground max-w-3xl">{report.summary}</p>
              )}
            </div>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto tabler-scroll -mx-2 px-2 pt-1">
          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-lg bg-muted/40 animate-pulse" />)}
            </div>
          ) : !report ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-[13px] text-muted-foreground">
              Report not found.
            </div>
          ) : (
            <ul className="space-y-3 pb-2">
              {(report.findings || []).map((f) => (
                <FindingRow
                  key={f.id}
                  finding={f}
                  onAskToFix={setActiveFinding}
                  submitting={submitting}
                />
              ))}
            </ul>
          )}
        </div>
        {activeFinding && (
          <AskToFixDialog
            finding={activeFinding}
            onClose={() => setActiveFinding(null)}
            onSubmit={askToFix}
            submitting={submitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SecurityReports() {
  const [reports, setReports] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/security/reports"),
        api.get("/security/summary"),
      ]);
      setReports(r.data.reports || []);
      setSummary(s.data);
    } catch { setReports([]); setSummary(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalFindings = summary?.finding_count ?? (reports || []).reduce((a, r) => a + (r.finding_count || 0), 0);
  const critHigh = (summary?.open_critical || 0) + (summary?.open_high || 0);

  return (
    <div className="space-y-5" data-testid="security-reports-page">
      <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1 flex-wrap">
        <Link to="/" className="hover:text-foreground">Dashboard</Link>
        <span aria-hidden>›</span>
        <span className="text-foreground font-medium inline-flex items-center gap-1">
          <ShieldAlert className="w-3.5 h-3.5 text-amber-500" /> Analytics · Security
        </span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-5 h-5 text-amber-500" />
          </div>
          <div className="min-w-0">
            <h1 className="text-[24px] font-semibold tracking-tight leading-tight">Security Reports</h1>
            <p className="text-[13px] text-muted-foreground max-w-2xl mt-1">
              Emergent security-scan reports with action items and remediation status. Ask Emergent to fix any finding — every request is logged with an auditable timeline.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="gap-1.5" data-testid="security-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="security-refresh-btn">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Reports" value={reports?.length ?? "—"} tone="primary" icon={ShieldCheck} sub="All-time" />
        <KpiCard label="Total findings" value={totalFindings} tone="primary" icon={AlertTriangle} sub="Across all reports" />
        <KpiCard label="Open critical" value={summary?.open_critical ?? 0} tone={(summary?.open_critical || 0) > 0 ? "rose" : "emerald"} icon={ShieldAlert} sub="Highest priority" />
        <KpiCard label="Open high" value={summary?.open_high ?? 0} tone={(summary?.open_high || 0) > 0 ? "orange" : "emerald"} icon={AlertTriangle} sub="Fix within week" />
      </div>

      {critHigh > 0 && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 text-rose-800 dark:text-rose-200 p-3 text-[13px] inline-flex items-start gap-2 max-w-4xl">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>{critHigh}</strong> open critical / high findings across your latest scans. Click a report to review and queue a fix.
          </div>
        </div>
      )}

      <div className="space-y-3" data-testid="security-report-list">
        {loading && (!reports || reports.length === 0) && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-muted/40 animate-pulse" />)}
          </div>
        )}
        {!loading && reports?.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-[13px] text-muted-foreground">
            No security reports yet.
          </div>
        )}
        {(reports || []).map((r) => (
          <ReportCard key={r.id} report={r} onOpen={() => setActiveId(r.id)} />
        ))}
      </div>

      {activeId && <ReportDrawer reportId={activeId} onClose={() => setActiveId(null)} />}
    </div>
  );
}

function ReportCard({ report, onOpen }) {
  const sev = report.severity_counts || {};
  const chips = ["critical", "high", "medium", "low", "info"]
    .filter((k) => (sev[k] || 0) > 0);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left rounded-2xl border border-border bg-card p-4 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
      data-testid={`security-report-card-${report.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <SeverityBadge severity={report.severity} />
            <span className="text-[10.5px] uppercase tracking-wide font-semibold rounded-full border border-border bg-muted/50 px-2 py-0.5">
              {report.source}
            </span>
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {fmtMDTShort(report.generated_at)}
            </span>
          </div>
          <h3 className="mt-1.5 text-[15px] font-semibold leading-snug group-hover:text-primary transition-colors">{report.title}</h3>
          <p className="mt-1 text-[12.5px] text-muted-foreground line-clamp-2 max-w-3xl">{report.summary}</p>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <span className="text-[11.5px] text-muted-foreground">
              <strong className="text-foreground">{report.finding_count}</strong> finding{report.finding_count === 1 ? "" : "s"}
            </span>
            {chips.map((k) => (
              <span key={k} className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold",
                SEVERITY_TONES[k],
              )}>
                <span className="tabular-nums">{sev[k]}</span> {k}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {typeof report.score === "number" && (
            <div className="rounded-lg border border-border bg-background px-3 py-1.5 text-right">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">Score</div>
              <div className="text-[18px] font-bold tabular-nums leading-none">{report.score}<span className="text-[11px] text-muted-foreground font-normal">/100</span></div>
            </div>
          )}
          <span className="text-[11.5px] text-primary inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            Open <ChevronRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </button>
  );
}
